import posthog from 'posthog-js';

const APP = 'company-dash';
const ENVIRONMENT = import.meta.env.VITE_ENV || 'unknown';
const POSTHOG_TOKEN = import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN;
const POSTHOG_HOST = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;
const POSTHOG_ENABLED = ENVIRONMENT === 'production' && Boolean(POSTHOG_TOKEN && POSTHOG_HOST);

let posthogInitialized = false;
let currentCompanyId;

const commonProperties = () => ({
  app: APP,
  environment: ENVIRONMENT,
  ...(import.meta.env.VITE_APP_VERSION && { app_version: import.meta.env.VITE_APP_VERSION }),
});

const sizeBucketFallback = (count) => {
  if (count == null) return undefined;
  if (count <= 10) return '1-10';
  if (count <= 50) return '11-50';
  if (count <= 200) return '51-200';
  if (count <= 500) return '201-500';
  return '500+';
};

const resolveGroupProperties = (groups) => {
  const userGroups = Array.isArray(groups) ? [...new Set(groups)] : [];

  return {
    user_groups: userGroups,
    group_employee: userGroups.includes('Dipendente'),
    group_supervisor: userGroups.includes('Responsabile'),
    group_safety_manager: userGroups.includes('ResponsabileSicurezza'),
    group_timesheet_manager: userGroups.includes('Timesheet'),
    group_company: userGroups.includes('Azienda'),
    group_operator: userGroups.includes('Operatore'),
    group_studio: userGroups.includes('Studio'),
    group_super_admin: userGroups.includes('SuperAdmin'),
  };
};

const resolveCompanyMembershipProperties = (userData) => {
  const companyIds = Object.keys(userData?.companies || {}).map(String);

  return {
    company_ids: companyIds,
    company_count: companyIds.length,
  };
};

export const initAnalytics = () => {
  if (POSTHOG_ENABLED && !posthogInitialized) {
    posthog.init(POSTHOG_TOKEN, {
      api_host: POSTHOG_HOST,
      defaults: '2026-01-30',
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      capture_performance: false,
      capture_dead_clicks: false,
      capture_heatmaps: false,
      disable_session_recording: true,
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: false,
      },
    });
    posthog.register(commonProperties());
    posthogInitialized = true;
  }
};

export const setCompanyContext = (userData, groups = []) => {
  if (!userData) return;

  const personProperties = {
    ...resolveGroupProperties(groups),
    ...resolveCompanyMembershipProperties(userData),
  };

  if (posthogInitialized) {
    posthog.setPersonProperties(personProperties);
  }

  const companyId = userData.current_company;
  if (companyId == null) {
    currentCompanyId = undefined;
    if (posthogInitialized) {
      posthog.unregister('company_id');
      posthog.resetGroups();
    }
    return;
  }

  currentCompanyId = String(companyId);
  const company = userData.companies?.[companyId] || {};
  const sizeBucket = company.employees_count_bucket || sizeBucketFallback(company.employees_count);

  if (posthogInitialized) {
    const companyProperties = {
      company_id: currentCompanyId,
      environment: ENVIRONMENT,
      ccnl_code: company.ccnl?.code ?? null,
      ...(company.name && { name: company.name }),
      ...(company.employees_count != null && { company_size: company.employees_count }),
      ...(sizeBucket && { company_size_bucket: sizeBucket }),
    };

    posthog.register({
      ...commonProperties(),
      company_id: currentCompanyId,
    });
    posthog.group('company', currentCompanyId, companyProperties);
  }
};

export const identifyUser = (userAttributes, userData, groups = []) => {
  if (!userAttributes?.sub || !userData) return;

  if (posthogInitialized) {
    const name = [userAttributes.given_name, userAttributes.family_name].filter(Boolean).join(' ');

    posthog.identify(`${ENVIRONMENT}:${userAttributes.sub}`, {
      ...(userAttributes.email && { email: userAttributes.email }),
      ...(name && { name }),
    });
  }

  setCompanyContext(userData, groups);
};

export const track = (eventName, properties = {}) => {
  const eventProperties = {
    ...properties,
    ...commonProperties(),
    ...(currentCompanyId && { company_id: currentCompanyId }),
  };

  if (posthogInitialized) {
    posthog.capture(eventName, eventProperties);
  }
};

export const resetAnalytics = () => {
  currentCompanyId = undefined;

  if (posthogInitialized) {
    posthog.reset();
    posthog.register(commonProperties());
  }
};
